import { FormGroup } from '@angular/forms';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { addRemoveError, getControl, getGroup } from '@shared/helpers/form.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import {
    MyAllAmminUnicoEntity,
    MyAllElemCollegioSindacaleEntity,
    MyAllElemConsiglioAmministrazioneEntity,
    MyAllElemLiquidatoriEntity,
    MySpdElemConsiglioGestioneEntity,
    MySpdElemConsiglioSorveglianzaEntity,
    MySpmElemComitatoGestioneEntity,
} from '../../../models/interfaces/revisione.interface';
import { getMonth, getYear, isAfter, isBefore, parseISO } from 'date-fns';
import { toNum } from '@shared/helpers/math.helper';
import { CkNormeValuesEnum } from '../../amm-controllo/common/consts/ckNormeValues.enum';
import { isNilOrEmpty, toBoolean } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { isEqual } from 'lodash-es';

export const conclusioniCkProposteCommRules: FormGroupRules = {
    my_CkProposte: (form: FormGroup) => {
        const conclusioniGroup = getGroup(form, formPaths.conclusioni);
        const datiIdentificativiGroup = getGroup(form, formPaths.datiIdentificativi);
        const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);
        const naturaMutualisticaGroup = getGroup(form, formPaths.naturaMutualistica);
        const schedaCoopSocialiGroup = getGroup(form, formPaths.schedaCoopSociali);
        const effettivitaBaseSocialeGroup = getGroup(form, formPaths.baseSociale);
        const bilancioGroup = getGroup(form, formPaths.bilancio);
        const altreNotizieGroup = getGroup(form, formPaths.altreNotizie);
        const contribLegge59Group = getGroup(form, formPaths.contributoLegge59);
        const schedaPrevalenzaGroup = getGroup(naturaMutualisticaGroup, 'my_SchedaPrevalenza');
        const normeSrlGroup = getGroup(amministrazioneControlloGroup, 'my_NormeSrl');
        const normeSpoGroup = getGroup(amministrazioneControlloGroup, 'my_NormeSpaOrdinario');
        const normeSpmGroup = getGroup(amministrazioneControlloGroup, 'my_NormeSpaMonistico');
        const normeSpdGroup = getGroup(amministrazioneControlloGroup, 'my_NormeSpaDualistico');

        const my_CkProposteControl = getControl(conclusioniGroup, 'my_CkProposte');
        const ck: string = my_CkProposteControl.value;

        const my_CkFunzionamPEC: string = getControl(datiIdentificativiGroup, 'my_CkFunzionamPEC').value;
        const my_DataTermine: string = getControl(datiIdentificativiGroup, 'my_DataTermine').value;
        const my_DataCompilaz: string = getControl(conclusioniGroup, 'my_DataCompilaz').value;
        const my_CkScadenzaSocieta: string = getControl(datiIdentificativiGroup, 'my_CkScadenzaSocieta').value;
        const my_DataAssembleaLiquidazVolontaria: string = getControl(amministrazioneControlloGroup, 'my_DataAssembleaLiquidazVolontaria').value;
        const my_CkScopoMutual: string = getControl(naturaMutualisticaGroup, 'my_CkScopoMutual').value;
        const my_CkAmmessiSociFinanz: string = getControl(schedaCoopSocialiGroup, 'my_CkAmmessiSociFinanz').value;
        const my_SociGiuCoopData: string = getControl(effettivitaBaseSocialeGroup, 'my_SociGiuCoopData').value;
        const my_CkTrattasiDiConsorzio: string = getControl(schedaCoopSocialiGroup, 'my_CkTrattasiDiConsorzio').value;
        const my_TipoSezEnte: string = getControl(datiIdentificativiGroup, 'my_TipoSezEnte').value;
        const my_CkNuovoSocioCatSpec: string = getControl(naturaMutualisticaGroup, 'my_CkNuovoSocioCatSpec').value;
        const my_SociSpecData: string = getControl(effettivitaBaseSocialeGroup, 'my_SociSpecData').value;
        const my_CkObbligoBilSoc: string = getControl(bilancioGroup, 'my_CkObbligoBilSoc').value;
        const my_CkRedazBilSociale: string = getControl(bilancioGroup, 'my_CkRedazBilSociale').value;
        const my_CkRistorni: string = getControl(bilancioGroup, 'my_CkRistorni').value;
        const my_ProvinciaSedeLegale: string = getControl(datiIdentificativiGroup, 'my_ProvinciaSedeLegale').value;
        const my_CkNorme: string = getControl(amministrazioneControlloGroup, 'my_CkNorme').value;
        const my_CkSrlCoop9soci: string = getControl(normeSrlGroup, 'my_CkSrlCoop9soci').value;
        const my_CkSrlCoop20soci: string = getControl(normeSrlGroup, 'my_CkSrlCoop20soci').value;
        const my_SociFisCoopPrimo: string = getControl(effettivitaBaseSocialeGroup, 'my_SociFisCoopPrimo').value;
        const my_SociGiuCoopPrimo: string = getControl(effettivitaBaseSocialeGroup, 'my_SociGiuCoopPrimo').value;
        const my_SociSpecPrimo: string = getControl(effettivitaBaseSocialeGroup, 'my_SociSpecPrimo').value;
        const my_IdAssegnazione: string = getControl(form, 'my_IdAssegnazione').value;
        const my_SrlScadenzaAmministratori: string = getControl(normeSrlGroup, 'my_SrlScadenzaAmministratori').value;
        const my_CkSrlTipoAmministrazione: string = getControl(normeSrlGroup, 'my_CkSrlTipoAmministrazione').value;
        const my_CkSpoTipoAmministrazione: string = getControl(normeSpoGroup, 'my_CkSpoTipoAmministrazione').value;
        const my_AllElemConsiglioAmministrazione: MyAllElemConsiglioAmministrazioneEntity[] = getControl(
            amministrazioneControlloGroup,
            'my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione'
        ).value;
        const my_CkSpmTipoAmministrazione: string = getControl(normeSpmGroup, 'my_CkSpmTipoAmministrazione').value;
        const my_SpdElemConsiglioGestione: MySpdElemConsiglioGestioneEntity[] = getControl(normeSpdGroup, 'my_SpdConsiglioGestione.my_SpdElemConsiglioGestione').value;
        const my_ControlloRevLegali: string = getControl(amministrazioneControlloGroup, 'my_ControlloRevLegali').value;
        const my_AllElemCollegioSindacale: MyAllElemCollegioSindacaleEntity[] = getControl(
            amministrazioneControlloGroup,
            'my_AllCollegioSindacale.my_AllElemCollegioSindacale'
        ).value;
        const my_CkSrlAffidamContrCont: string = getControl(normeSrlGroup, 'my_CkSrlAffidamContrCont').value;
        const my_ControlloSoci: string = getControl(amministrazioneControlloGroup, 'my_ControlloSoci').value;
        const my_CkSpoAffidamContrCont: string = getControl(normeSpoGroup, 'my_CkSpoAffidamContrCont').value;
        const my_SpdElemConsiglioSorveglianza: MySpdElemConsiglioSorveglianzaEntity[] = getControl(
            normeSpdGroup,
            'my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza'
        ).value;
        const my_CkSpdAffidamContrCont: string = getControl(normeSpdGroup, 'my_CkSpdAffidamContrCont').value;
        const my_SpmElemComitatoGestione: MySpmElemComitatoGestioneEntity[] = getControl(normeSpmGroup, 'my_SpmComitatoGestione.my_SpmElemComitatoGestione').value;
        const my_CkSpmAffidamContrCont: string = getControl(normeSpmGroup, 'my_CkSpmAffidamContrCont').value;
        const my_TotPrevalSoci: string = getControl(schedaPrevalenzaGroup, 'my_TotPrevalSoci').value;
        const my_TotPrevalTot: string = getControl(schedaPrevalenzaGroup, 'my_TotPrevalTot').value;
        const my_CkValoreQuantita: string = getControl(schedaPrevalenzaGroup, 'my_CkValoreQuantita').value;
        const my_NumBilanciInExcel: string = getControl(bilancioGroup, 'my_NumBilanciInExcel').value;
        const my_RegimeDerogatorioProvalenza: string = getControl(datiIdentificativiGroup, 'my_RegimeDerogatorioProvalenza').value;
        const my_AllElemLiquidatori: MyAllElemLiquidatoriEntity[] = getControl(amministrazioneControlloGroup, 'my_AllLiquidatori.my_AllElemLiquidatori').value;
        const my_CkVersamEffett: string = getControl(contribLegge59Group, 'my_CkVersamEffett').value;
        const my_ImportoComplessivo: string = getControl(contribLegge59Group, 'my_ImportoComplessivo').value;
        const my_ImportoComplessivoAnno1: string = getControl(contribLegge59Group, 'my_ImportoComplessivoAnno1').value;
        const my_CkNormeSocioLavorat: string = getControl(effettivitaBaseSocialeGroup, 'my_CkNormeSocioLavorat').value;
        const my_UnitaSociUtil: string = getControl(effettivitaBaseSocialeGroup, 'my_UnitaSociUtil').value;
        const my_UnitaNonSociUtil: string = getControl(effettivitaBaseSocialeGroup, 'my_UnitaNonSociUtil').value;
        const my_CkIrregSanabili: string = getControl(conclusioniGroup, 'my_CkIrregSanabili').value;
        const my_TipoAdesione: string = getControl(datiIdentificativiGroup, 'my_TipoAdesione').value;
        const my_CkEnteTenutoCertBil: string = getControl(bilancioGroup, 'my_CkEnteTenutoCertBil').value;
        const my_CkProvvedeCertBil: string = getControl(bilancioGroup, 'my_CkProvvedeCertBil').value;
        const my_RagSocRevIncaricataConvenzionata: string = getControl(bilancioGroup, 'my_RagSocRevIncaricataConvenzionata').value;
        const my_DataRevisioneDal: string = getControl(datiIdentificativiGroup, 'my_DataRevisioneDal').value;
        const my_DataCostituz: string = getControl(datiIdentificativiGroup, 'my_DataCostituz').value;
        const my_DescProvinciaSedeLegale: string = getControl(datiIdentificativiGroup, 'my_DescProvinciaSedeLegale').value;
        const my_DataChiusura2: string = getControl(getGroup(form, 'my_SezioneI__Rilevazione.my_DatiBilancio'), 'my_DataChiusura2').value;
        const my_CkLibriSociali: string = getControl(altreNotizieGroup, 'my_CkLibriSociali').value;
        const my_CkLibriSocBolloVid: string = getControl(altreNotizieGroup, 'my_CkLibriSocBolloVid').value;
        const my_CkLibroOrgAmm: string = getControl(altreNotizieGroup, 'my_CkLibroOrgAmm').value;
        const my_CkLibroOrgContr: string = getControl(altreNotizieGroup, 'my_CkLibroOrgContr').value;
        const my_CkLibroGioInv: string = getControl(altreNotizieGroup, 'my_CkLibroGioInv').value;
        const my_CkLibroGioInvAgg: string = getControl(altreNotizieGroup, 'my_CkLibroGioInvAgg').value;
        const my_CalcObbOrganoContr: string = getControl(amministrazioneControlloGroup, 'my_CalcObbOrganoContr').value;
        const my_Anno2: string = getControl(datiIdentificativiGroup, 'my_Anno2').value;
        const my_DataScadenzaDiffida: string = getControl(conclusioniGroup, 'my_DataScadenzaDiffida').value;
        const my_ControlloRevisoreContabileSocio: string = getControl(amministrazioneControlloGroup, 'my_ControlloRevisoreContabileSocio').value;
        const my_ScSocObbl: string = getControl(schedaCoopSocialiGroup, 'my_ScSocObbl').value;
        const my_ckCoopSvolgeCommEqSolid: string = getControl(schedaCoopSocialiGroup, 'my_ckCoopSvolgeCommEqSolid').value;
        const my_CkServSocioSanit: string = getControl(schedaCoopSocialiGroup, 'my_CkServSocioSanit').value;
        const my_ckAttSocEduPrevCommEqSolid: string = getControl(schedaCoopSocialiGroup, 'my_ckAttSocEduPrevCommEqSolid').value;
        const my_ckCommEqSolidStrumAttSocEdu: string = getControl(schedaCoopSocialiGroup, 'my_ckCommEqSolidStrumAttSocEdu').value;
        const my_ckCommEqSolidDocumStrumAtt: string = getControl(schedaCoopSocialiGroup, 'my_ckCommEqSolidDocumStrumAtt').value;
        const my_PercentSociVolont: string = getControl(schedaCoopSocialiGroup, 'my_PercentSociVolont').value;
        const my_AllAmminUnico: MyAllAmminUnicoEntity[] = getControl(amministrazioneControlloGroup, 'my_AllAmminUnico').value;
        const my_consentiVerbaleSenzaBilancio: string = getControl(form, 'my_consentiVerbaleSenzaBilancio').value;

        const dateCompilaz = parseISO(my_DataCompilaz);
        const beforeOrEqual = isBefore(parseISO(my_DataTermine), dateCompilaz) || isEqual(parseISO(my_DataTermine), dateCompilaz);
        const afterOrEqual = isAfter(dateCompilaz, parseISO('2020-09-01')) || isEqual(dateCompilaz, parseISO('2020-09-01'));
        const afterOrEqual2019 = isAfter(dateCompilaz, parseISO('2019-01-01')) || isEqual(dateCompilaz, parseISO('2019-01-01'));
        const afterOrEqual2018 = isAfter(dateCompilaz, parseISO('2018-09-01')) || isEqual(dateCompilaz, parseISO('2018-09-01'));
        const after2018 = isAfter(dateCompilaz, parseISO('2018-04-04'));
        const annoDataCostituz = getYear(new Date(my_DataCostituz)) + 1;
        const meseDataCostituz = getMonth(new Date(my_DataChiusura2));
        const afterDataRevisioneDal = isAfter(new Date(my_DataRevisioneDal), new Date('31' + String(meseDataCostituz) + String(annoDataCostituz)));
        const annoConcatenazione = toNum(my_Anno2) + 1;
        const dataDiffida = isAfter(new Date(my_DataScadenzaDiffida), new Date(String(annoConcatenazione) + '-05-31'));
        const sommaSoci = toNum(my_SociFisCoopPrimo) + toNum(my_SociGiuCoopPrimo) + toNum(my_SociSpecPrimo);
        const province = ['14', '31', '34', '55', '62', '67', '74', '76', '78'];

        const errors = my_CkProposteControl.errors ? my_CkProposteControl.errors : {};

        my_AllAmminUnico
            .map((e) => e.my_AllAmmUnCkSocio)
            .forEach((my_AllAmmUnCkSocio) => {
                addRemoveError(ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSrl && my_AllAmmUnCkSocio !== '1', errors, 'my_CkProposte_SRL_amminUnico', {
                    status: 'Attenzione: verificare norme SRL',
                });
                addRemoveError(ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario && my_AllAmmUnCkSocio !== '1', errors, 'my_CkProposte_SPO_amminUnico', {
                    status: 'Attenzione: verificare norme SPAOrd',
                });
            });

        my_AllElemCollegioSindacale
            .map((e) => e.my_AllCollSindCarica)
            .forEach((my_AllCollSindCarica) => {
                addRemoveError(my_AllCollSindCarica === '073' && my_CkNorme === CkNormeValuesEnum.normeSrl && ck !== '3', errors, 'my_CkProposte_collSindCaricaSrl', {
                    status: 'Declinare ulteriormente: sindaco effettivo o sindaco supplente.',
                });
                addRemoveError(my_AllCollSindCarica === '073' && my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario && ck !== '3', errors, 'my_CkProposte_collSindCaricaSpo', {
                    status: 'Declinare ulteriormente: sindaco effettivo o sindaco supplente.',
                });
            });

        addRemoveError(my_CkFunzionamPEC === '0' && ck === '1', errors, 'my_CkProposte_PEC', {
            status: "PEC: Non può essere rilasciato l'attestato se la PEC della cooperativa non è univoca ed attiva",
        });
        addRemoveError(ck === '1' && beforeOrEqual && my_CkScadenzaSocieta === '0' && isNilOrEmpty(my_DataAssembleaLiquidazVolontaria), errors, 'my_CkProposte_dataTermine', {
            status: "Attenzione: verificare la data di termine società non è possibile rilasciare l'attestato di revisione",
        });
        addRemoveError(my_CkScopoMutual === '0' && ck === '1', errors, 'my_CkProposte_scopoMutual', {
            status: `Scopo Mutualistico: La revisione non può terminare con il rilascio dell'attestato se lo statuto non prevede lo scopo mutualisti`,
        });
        addRemoveError(
            ck === '1' && my_TipoSezEnte === '2' && my_CkAmmessiSociFinanz === '0' && toNum(my_SociGiuCoopData) > 0 && my_CkTrattasiDiConsorzio !== '1',
            errors,
            'my_CkProposte_sociGiu',
            {
                status: `Cooperative sociali: è possibile ammettere, ai sensi dell'art. 11 della L. 381/1991, persone giuridiche pubbliche o private, a condizione che nel loro statuto sia previsto il finanziamento o lo sviluppo delle attività di tali cooperative.`,
            }
        );
        addRemoveError(ck === '1' && my_TipoSezEnte === '5' && toNum(my_SociGiuCoopData) > 0, errors, 'my_CkProposte_tipoSezEnte5', {
            status: 'Cooperativa Produzione e Lavoro: verificare la presenza di soci cooperatori persone giuridiche.',
        });
        addRemoveError(ck === '1' && my_CkNuovoSocioCatSpec === '0' && toNum(my_SociSpecData) > 0, errors, 'my_CkProposte_sociSpec', {
            status: `Soci Speciali: La revisione non si può concludere con l'attestato perché lo statuto non prevede la presenza di soci speciali`,
        });
        addRemoveError(
            ck === '1' && my_CkObbligoBilSoc === '1' && my_CkRedazBilSociale === '0' && province.includes(my_ProvinciaSedeLegale) && afterOrEqual,
            errors,
            'my_CkProposte_bilSocProv',
            { status: 'Bilancio Sociale: la cooperativa è tenuta a redigere il Bilancio Sociale' }
        );
        addRemoveError(
            ck === '1' && my_CkObbligoBilSoc === '1' && my_CkRedazBilSociale === '0' && !province.includes(my_ProvinciaSedeLegale),
            errors,
            'my_CkProposte_bilSocNoProv',
            { status: 'Bilancio Sociale: la cooperativa è tenuta a redigere il Bilancio Sociale' }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSrl &&
                my_CkSrlCoop9soci === '0' &&
                my_CkSrlCoop20soci === '0' &&
                sommaSoci > 19 &&
                my_IdAssegnazione !== '226931' &&
                my_IdAssegnazione !== '268623',
            errors,
            'my_CkProposte_srlCoop',
            { status: 'La cooperativa non può adottare il sistema Srl' }
        );
        addRemoveError(
            ck === '1' && afterOrEqual2019 && toNum(my_SrlScadenzaAmministratori) === 99 && my_CkSrlTipoAmministrazione !== '3' && my_CkNorme === CkNormeValuesEnum.normeSrl,
            errors,
            'my_CkProposte_srlScadenza',
            { status: 'Per le modifiche introdotte dalla legge di bilancio 2018 la cooperativa deve definire un termine della nomina' }
        );
        addRemoveError(ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSrl && my_CkSrlTipoAmministrazione === '2' && afterOrEqual2018, errors, 'my_CkProposte_srlAmminUnico', {
            status: `Per le modifiche introdotte dalla legge di bilancio 2018, l'amministrazione della cooperativa non può essere affidata ad un amministratore unico`,
        });
        addRemoveError(
            ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario && my_CkSpoTipoAmministrazione === '2' && afterOrEqual2018,
            errors,
            'my_CkProposte_spoAmminUnico',
            { status: `Per le modifiche introdotte dalla legge di bilancio 2018, l'amministrazione della cooperativa non può essere affidata ad un amministratore unico` }
        );
        addRemoveError(
            ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSrl && my_CkSrlTipoAmministrazione === '1' && afterOrEqual2018 && my_AllElemConsiglioAmministrazione?.length < 3,
            errors,
            'my_CkProposte_srlCda',
            { status: `Per le modifiche introdotte dalla legge di bilancio 2018, l'amministrazione della cooperativa non può essere affidata ad un CdA con meno di tre componenti` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario &&
                my_CkSpoTipoAmministrazione === '1' &&
                afterOrEqual2018 &&
                my_AllElemConsiglioAmministrazione?.length < 3,
            errors,
            'my_CkProposte_spoCda',
            { status: `Per le modifiche introdotte dalla legge di bilancio 2018, l'amministrazione della cooperativa non può essere affidata ad un CdA con meno di tre componenti` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSpaMonistico &&
                my_CkSpmTipoAmministrazione === '1' &&
                afterOrEqual2018 &&
                my_AllElemConsiglioAmministrazione?.length < 3,
            errors,
            'my_CkProposte_spmCda',
            { status: `Per le modifiche introdotte dalla legge di bilancio 2018, l'amministrazione della cooperativa non può essere affidata ad un CdA con meno di tre componenti` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSpaDualistico &&
                my_SpdElemConsiglioGestione?.length > 0 &&
                my_SpdElemConsiglioGestione?.length < 3 &&
                afterOrEqual2018,
            errors,
            'my_CkProposte_spdConsiglioGestione',
            {
                status: `Per le modifiche introdotte dalla legge di bilancio 2018, l'amministrazione della cooperativa non può essere affidata ad un Consiglio di Gestione con meno di tre componenti`,
            }
        );
        addRemoveError(
            ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSrl && my_ControlloRevLegali === '0' && my_AllElemCollegioSindacale?.length > 0,
            errors,
            'my_CkProposte_srlRevLegali',
            { status: `Se tutti i componenti dell'Organo di Controllo non sono revisori legali non può essere rilasciato l'attestato di revisione` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSrl &&
                my_CkSrlAffidamContrCont === '5' &&
                toNum(my_ControlloSoci) > 0 &&
                my_AllElemCollegioSindacale?.length > 0 &&
                my_IdAssegnazione !== '270738',
            errors,
            'my_CkProposte_srlSoci',
            { status: `Se la Revisione Legale è affidata all'Organo di Controllo, i componenti non possono essere soci` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSrl &&
                my_CkSrlAffidamContrCont === '5' &&
                toNum(my_ControlloRevLegali) < my_AllElemCollegioSindacale?.length &&
                my_AllElemCollegioSindacale?.length > 0,
            errors,
            'my_CkProposte_srlTuttiRevLegali',
            { status: `Se la Revisione Legale è affidata all'Organo di Controllo, tutti i componenti devono essere revisori legali` }
        );
        addRemoveError(
            ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario && my_ControlloRevLegali === '0' && my_AllElemCollegioSindacale?.length > 0,
            errors,
            'my_CkProposte_spoRevLegali',
            { status: `Se tutti i componenti dell'Organo di Controllo non sono revisori legali non può essere rilasciato l'attestato di revisione` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario &&
                my_CkSpoAffidamContrCont === '5' &&
                toNum(my_ControlloSoci) > 0 &&
                my_AllElemCollegioSindacale?.length > 0,
            errors,
            'my_CkProposte_spoSoci',
            { status: `Se la Revisione Legale è affidata all'Organo di Controllo, i componenti non possono essere soci` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario &&
                my_CkSpoAffidamContrCont === '5' &&
                toNum(my_ControlloRevLegali) < my_AllElemCollegioSindacale?.length &&
                my_AllElemCollegioSindacale?.length > 0,
            errors,
            'my_CkProposte_spoTuttiRevLegali',
            { status: `Se la Revisione Legale è affidata all'Organo di Controllo, tutti i componenti devono essere revisori legali` }
        );
        addRemoveError(
            ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSpaDualistico && my_ControlloRevLegali === '0' && my_SpdElemConsiglioSorveglianza?.length > 0,
            errors,
            'my_CkProposte_spdRevLegali',
            { status: `Se tutti i componenti del Consiglio di Sorveglianza non sono revisori legali non può essere rilasciato l'attestato di revisione` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSpaDualistico &&
                my_CkSpdAffidamContrCont === '4' &&
                toNum(my_ControlloSoci) > 0 &&
                my_SpdElemConsiglioSorveglianza?.length > 0,
            errors,
            'my_CkProposte_spdSoci',
            { status: `Se la Revisione Legale è affidata al Consiglio di Sorveglianza, nessun componente può essere socio` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSpaDualistico &&
                my_CkSpdAffidamContrCont === '4' &&
                toNum(my_ControlloRevLegali) < my_SpdElemConsiglioSorveglianza?.length &&
                my_SpdElemConsiglioSorveglianza?.length > 0,
            errors,
            'my_CkProposte_spdTuttiRevLegali',
            { status: 'Se la Revisione Legale è affidata al Consiglio di Sorveglianza, tutti i componenti devono essere revisori legali' }
        );
        addRemoveError(
            ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSpaMonistico && my_ControlloRevLegali === '0' && my_SpmElemComitatoGestione?.length > 0,
            errors,
            'my_CkProposte_spmRevLegali',
            { status: `Se tutti i componenti del Comitato di Controllo non sono revisori legali non può essere rilasciato l'attestato di revisione` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSpaMonistico &&
                my_CkSpmAffidamContrCont === '6' &&
                toNum(my_ControlloSoci) > 0 &&
                my_SpmElemComitatoGestione?.length > 0,
            errors,
            'my_CkProposte_spmSoci',
            { status: `Se la Revisione Legale è affidata al Comitato di controllo, nessun componente può essere socio` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkNorme === CkNormeValuesEnum.normeSpaMonistico &&
                my_CkSpmAffidamContrCont === '6' &&
                toNum(my_ControlloRevLegali) < my_SpmElemComitatoGestione?.length &&
                my_SpmElemComitatoGestione?.length > 0,
            errors,
            'my_CkProposte_spmTuttiRevLegali',
            { status: 'Se la Revisione Legale è affidata al Comitato di Controllo, tutti i componenti devono essere revisori legali' }
        );
        addRemoveError(
            ck === '1' &&
                toNum(my_TotPrevalSoci) === 0 &&
                toNum(my_TotPrevalTot) > 0 &&
                my_CkValoreQuantita !== 'Q' &&
                toNum(my_NumBilanciInExcel) === 2 &&
                my_RegimeDerogatorioProvalenza === '1' &&
                !my_AllElemLiquidatori?.length,
            errors,
            'my_CkProposte_prevalSoci',
            { status: `La revisione dell'ente non può terminare con l'attestato. Verificare lo scambio mutualistico` }
        );
        // Due errorCondition InfoPath indipendenti con lo stesso messaggio (esercizio corrente / precedente): stessa chiave, quindi una sola addRemoveError in OR.
        addRemoveError(
            ck === '1' &&
                my_CkVersamEffett === '0' &&
                my_TipoSezEnte !== '11' &&
                (toNum(my_ImportoComplessivo) >= 10.33 || (toNum(my_ImportoComplessivoAnno1) >= 10.33 && after2018)),
            errors,
            'my_CkProposte_versam',
            { status: `La revisione non può terminare con l'attestato se l'ente cooperativo non ha correttamente effettuato i versamenti dovuti per il 3%` }
        );
        addRemoveError(ck === '1' && toNum(my_UnitaNonSociUtil) > 0 && toNum(my_UnitaSociUtil) === 0 && my_CkNormeSocioLavorat === '1', errors, 'my_CkProposte_unitaNonSoci', {
            status: 'Verificare lo scambio mutualistico',
        });
        addRemoveError(ck === '3' && my_CkIrregSanabili === '0', errors, 'my_CkProposte_irregSanabili', {
            status: 'In caso di diffida è obbligatorio specificare almeno una irregolarità sanabile al punto 57',
        });
        addRemoveError(ck !== '3' && my_CkIrregSanabili === '1', errors, 'my_CkProposte_irregSanabiliNoDiffida', {
            status: 'In caso di presenza di irregolarità sanabili è necessario irrogare la diffida',
        });
        addRemoveError(
            ck !== '3' &&
                my_TipoAdesione === 'UNICA' &&
                my_DescProvinciaSedeLegale !== 'AOSTA' &&
                my_IdAssegnazione !== '157199' &&
                my_CkEnteTenutoCertBil === '1' &&
                my_CkProvvedeCertBil === '1' &&
                my_RagSocRevIncaricataConvenzionata !== '1',
            errors,
            'my_CkProposte_certBil',
            { status: 'La cooperativa è certificabile, ad adesione unica, e non utilizza società convenzionate' }
        );
        addRemoveError(ck === '1' && my_CkLibriSociali === '0', errors, 'my_CkProposte_libriSociali', {
            status: `Se i Libri sociali obbligatori non sono stati regolarmente istituiti non è possibile rilasciare l'attestato di revisione`,
        });
        addRemoveError(ck === '1' && my_CkLibriSocBolloVid === '0', errors, 'my_CkProposte_libriSocBolloVid', {
            status: `Se i Libri Sociali non risultano correttamente bollati e vidimati inizialmente non è possibile rilasciare l'attestato di revisione`,
        });
        addRemoveError(ck === '1' && my_CkLibroOrgAmm === '0', errors, 'my_CkProposte_libroOrgAmm', {
            status: `Se nel Libro dell'Organo Amministrativo non risultano trascritte le delibere/determinazioni relative ai principali fatti gestionali e quelle relative all'ammissione-recesso-esclusione dei soci non è possibile rilasciare l'attestato di revisione`,
        });
        addRemoveError(ck === '1' && my_CkLibroOrgContr === '0' && my_AllElemCollegioSindacale?.length > 0, errors, 'my_CkProposte_libroOrgContr', {
            status: `Attenzione: se nel Libro dell'Organo di Controllo non risultano trascritti i verbali delle riunioni/verifiche da tenersi ogni 90 giorni non è possibile rilasciare l'attestato di revisione`,
        });
        addRemoveError(ck === '1' && my_CkLibroGioInv === '0', errors, 'my_CkProposte_libroGioInv', {
            status: `Attenzione: se il Libro Giornale ed il Libro Inventari non sono stati regolarmente istituiti non è possibile rilasciare l'attestato di revisione`,
        });
        addRemoveError(ck === '1' && my_CkLibroGioInvAgg === '0', errors, 'my_CkProposte_libroGioInvAgg', {
            status: `Attenzione: se il Libro Giornale ed il Libro Inventari non risultano correttamente numerati ed aggiornati non è possibile rilasciare l'attestato di revisione`,
        });
        addRemoveError(
            ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario && my_AllElemCollegioSindacale?.length < 3 && my_AllElemCollegioSindacale?.length > 0,
            errors,
            'my_CkProposte_spoCollegio3',
            { status: `SPO: La revisione dell'ente non può terminare con l'attestato.` }
        );
        addRemoveError(
            ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario && my_AllElemCollegioSindacale?.length < 1 && toBoolean(my_CalcObbOrganoContr),
            errors,
            'my_CkProposte_spoCollegio0',
            { status: `SPO: La revisione dell'ente non può terminare con l'attestato.` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkSrlAffidamContrCont === '8' &&
                toBoolean(my_CalcObbOrganoContr) &&
                my_CkNorme === CkNormeValuesEnum.normeSrl &&
                my_AllElemCollegioSindacale?.length < 1,
            errors,
            'my_CkProposte_srlObbContr8',
            { status: `SRL: La revisione dell'ente non può terminare con l'attestato.` }
        );
        addRemoveError(
            ck === '1' &&
                my_CkSrlAffidamContrCont === '7' &&
                toBoolean(my_CalcObbOrganoContr) &&
                my_CkNorme === CkNormeValuesEnum.normeSrl &&
                my_AllElemCollegioSindacale?.length < 1,
            errors,
            'my_CkProposte_srlObbContr7',
            { status: `SRL: La revisione dell'ente non può terminare con l'attestato.` }
        );
        addRemoveError(ck === '3' && dataDiffida, errors, 'my_CkProposte_dataDiffida', { status: 'Attenzione, la diffida non può protrarsi oltre il 31/05 del prossimo biennio.' });
        addRemoveError(ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSpaDualistico && my_CkSpdAffidamContrCont === '7', errors, 'my_CkProposte_spdNoRevLegale', {
            status: `Se la Revisione Legale non è affidata non può essere rilasciato l'attestato di revisione.`,
        });
        addRemoveError(ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSpaMonistico && my_CkSpmAffidamContrCont === '7', errors, 'my_CkProposte_spmNoRevLegale', {
            status: `Se la Revisione Legale non è affidata non può essere rilasciato l'attestato di revisione.`,
        });
        addRemoveError(ck === '1' && my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario && my_CkSpoAffidamContrCont === '7', errors, 'my_CkProposte_spoNoRevLegale', {
            status: `Se la Revisione Legale non è affidata non può essere rilasciato l'attestato di revisione.`,
        });
        addRemoveError(ck === '1' && toNum(my_ControlloRevisoreContabileSocio) > 0 && my_CkSrlAffidamContrCont === '2', errors, 'my_CkProposte_srlRevisoreSocio2', {
            status: `SRL: La revisione dell'ente non può terminare con l'attestato.`,
        });
        addRemoveError(ck === '1' && toNum(my_ControlloRevisoreContabileSocio) > 0 && my_CkSrlAffidamContrCont === '3', errors, 'my_CkProposte_srlRevisoreSocio3', {
            status: `SRL: La revisione dell'ente non può terminare con l'attestato.`,
        });
        addRemoveError(ck === '1' && toNum(my_ControlloRevisoreContabileSocio) > 0 && my_CkSpoAffidamContrCont === '3', errors, 'my_CkProposte_spoRevisoreSocio3', {
            status: `SPO: La revisione dell'ente non può terminare con l'attestato.`,
        });
        addRemoveError(ck === '1' && toNum(my_ControlloRevisoreContabileSocio) > 0 && my_CkSpoAffidamContrCont === '2', errors, 'my_CkProposte_spoRevisoreSocio2', {
            status: `SPO: La revisione dell'ente non può terminare con l'attestato.`,
        });
        addRemoveError(
            my_ScSocObbl === '1' &&
                my_ckCoopSvolgeCommEqSolid === '1' &&
                my_CkServSocioSanit === '1' &&
                (my_ckAttSocEduPrevCommEqSolid === '0' || my_ckCommEqSolidStrumAttSocEdu === '0' || my_ckCommEqSolidDocumStrumAtt === '0'),
            errors,
            'my_CkProposte_commEqSolid',
            { status: `La cooperativa svolge attività di commercio equo e solidale, verificare le informazioni fornite per rilasciare l'attestato.` }
        );
        addRemoveError(ck === '1' && toNum(my_PercentSociVolont) > 0.5 && toNum(my_PercentSociVolont) <= 1 && my_ScSocObbl === '1', errors, 'my_CkProposte_percentSociVolont', {
            status: 'Il numero di soci volontari costituisce più della metà del numero complessivo di soci.',
        });
        addRemoveError(
            ck === '1' && toNum(my_NumBilanciInExcel) === 0 && my_consentiVerbaleSenzaBilancio !== 'S' && afterDataRevisioneDal && my_IdAssegnazione !== '228062',
            errors,
            'my_CkProposte_noBilancio',
            { status: `Attenzione, non è stato caricato alcun bilancio. Contattare il capo area per il rilascio dell'attestato.` }
        );

        addRemoveError(ck === '1' && my_CkRistorni === '0', errors, 'my_CkProposte_ristorni', {
            status: `I ristorni non sono stati correttamente determinati e ripartiti`,
        });

        addRemoveError(isNilOrEmpty(ck), errors, 'my_CkProposte', {
            status: REQUIRED_STATUS,
        });

        my_CkProposteControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};
